/* Email HTML made safe and tidy to put in the page.
 *
 * **The page's Content-Security-Policy is what actually stops requests**
 * (Decision 27): nothing an email contains can make the browser contact
 * anybody, whether or not this file catches it. This file makes the result
 * LOOK right and can say what it removed: no broken image icons, a count of
 * what was blocked, and nothing left that could run or submit.
 *
 * `DOMParser` builds a document that loads nothing, so parsing hostile HTML
 * here is itself safe.
 */

/** Elements removed outright: anything that runs, loads, embeds or redirects. */
const DROP = "script, iframe, frame, frameset, object, embed, applet, link, meta, base, noscript, template, portal, frame, param";

/**
 * **Only these may be an image source**: part of the message itself, or bytes
 * already in the tab. A RELATIVE address is a request too: `<img src="x">`
 * resolves against the page and asks our own server for a path the email
 * chose, which a check for "http" never sees (the spike's worst finding).
 */
const LOCAL = /^\s*(data:image\/|blob:)/i;

const cidKey = (value) => value.replace(/^cid:/i, "").replace(/^<|>$/g, "").trim().toLowerCase();

/**
 * @param {string} html  the email's HTML
 * @param {Map<string,string>} cids  content id (lower case, no brackets) to blob URL
 * @param {{loadRemote?: boolean}} options  loadRemote keeps remote image
 *   addresses, for the isolated frame the reader can ask for (step 6)
 * @returns {{styles: string, body: string, blocked: number}}
 */
export function sanitize(html, cids = new Map(), options = {}) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  let blocked = 0;
  const keep = (src) => LOCAL.test(src) || (options.loadRemote && /^\s*https?:\/\//i.test(src));

  doc.querySelectorAll(DROP).forEach((element) => element.remove());

  for (const element of doc.querySelectorAll("*")) {
    for (const attribute of [...element.attributes]) {
      const name = attribute.name.toLowerCase();
      const value = attribute.value;

      if (name.startsWith("on") || name === "formaction" || name === "srcset" || name === "ping") {
        element.removeAttribute(attribute.name);
      } else if ((name === "href" || name === "src" || name === "xlink:href" || name === "action" || name === "data")
        && /^\s*(javascript|vbscript|data:text\/html)/i.test(value)) {
        element.removeAttribute(attribute.name);
      } else if ((name === "background" || name === "poster") && !keep(value)) {
        element.removeAttribute(attribute.name);
        blocked++;
      } else if (name === "style" && /url\s*\(/i.test(value)) {
        const [css, n] = stripUrls(value, cids, keep);
        element.setAttribute("style", css);
        blocked += n;
      }
    }
  }

  for (const image of doc.querySelectorAll("img, image, input[type=image], video, audio, source, track")) {
    const attribute = image.hasAttribute("src") ? "src" : image.hasAttribute("href") ? "href" : "xlink:href";
    const src = image.getAttribute(attribute) || "";

    if (/^\s*cid:/i.test(src)) {
      const url = cids.get(cidKey(src));
      if (url) image.setAttribute(attribute, url);
      else image.removeAttribute(attribute);
    } else if (src && !keep(src)) {
      blocked++;
      placeholder(doc, image);
    }
  }

  for (const link of doc.querySelectorAll("a[href]")) {
    // A link is the reader choosing to visit somewhere, in a new tab, telling
    // the site nothing about where they came from.
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  }

  // **An email's form must not be submittable at all.** The policy allows
  // forms to this site (the header's Sign out is one), so a form here would
  // post what the email put in it to us. Flattened into plain containers; the
  // controls stay visible and inert.
  for (const form of doc.querySelectorAll("form")) {
    const div = doc.createElement("div");
    div.append(...form.childNodes);
    form.replaceWith(div);
  }
  for (const control of doc.querySelectorAll("button, input, select, textarea")) {
    control.setAttribute("disabled", "");
    if (control.tagName === "BUTTON") control.setAttribute("type", "button");
  }

  let styles = "";
  for (const style of doc.querySelectorAll("style")) {
    let css = style.textContent.replace(/@import[^;]*;?/gi, () => {
      blocked++;
      return "";
    });
    const [stripped, n] = stripUrls(css, cids, keep);
    blocked += n;
    // Email CSS styles `html` and `body`; inside the shadow root those are our wrappers.
    css = stripped
      .replace(/(^|[\s,}>+~])html(?=[\s,{.:#[>+~])/g, "$1.mail-html")
      .replace(/(^|[\s,}>+~])body(?=[\s,{.:#[>+~])/g, "$1.mail-body");
    styles += `<style>${css.replace(/<\/style/gi, "")}</style>`;
    style.remove();
  }

  const bodyStyle = doc.body.getAttribute("style");
  if (bodyStyle) styles += `<style>.mail-body{${bodyStyle.replace(/[<{}]/g, "")}}</style>`;

  return { styles, body: doc.body.innerHTML, blocked };
}

/**
 * A blocked image becomes a labelled box of its declared size rather than an
 * `<img>` with no source, which Chrome draws as a broken image icon over the
 * alt text. One with no words and no real size (tracking pixels, spacers)
 * simply goes.
 */
function placeholder(doc, image) {
  const alt = (image.getAttribute("alt") || "").trim();
  const width = parseInt(image.getAttribute("width"), 10) || 0;
  const height = parseInt(image.getAttribute("height"), 10) || 0;

  if (image.tagName !== "IMG" || (!alt && (width < 40 || height < 40))) {
    image.remove();
    return;
  }

  const box = doc.createElement("span");
  box.className = "blocked-img";
  box.textContent = alt;
  if (width >= 40) box.style.width = `${width}px`;
  if (height >= 40) box.style.height = `${height}px`;
  image.replaceWith(box);
}

function stripUrls(css, cids, keep) {
  let blocked = 0;

  const out = css.replace(/url\s*\(\s*(['"]?)(.*?)\1\s*\)/gi, (whole, _quote, url) => {
    if (/^\s*cid:/i.test(url)) {
      const blob = cids.get(cidKey(url));
      return blob ? `url("${blob}")` : "none";
    }
    // In CSS any data: or blob: is local, fonts included (`font-src data:`).
    if (/^\s*(data:|blob:)/i.test(url) || keep(url)) return whole;
    blocked++;
    return "none";
  });

  return [out, blocked];
}
